import { ROLE_LABELS, type User } from '@ops/contracts'
import type { ReactNode } from 'react'
import { useId } from 'react'

import { iniciales } from '../lib/format'
import { formatPeriodLabel } from './periods'

/**
 * Barra superior: título de la vista, selector de período y quién sos.
 *
 * El período es del shell y no de cada pantalla: las seis vistas leen el mismo
 * 'YYYY-MM' del contexto, así que cambiar de mes cambia todo a la vez y no queda
 * una tabla mostrando junio al lado de un KPI de julio.
 */
export interface HeaderProps {
  user: User
  periodo: string
  periodos: readonly string[]
  onPeriodoChange: (periodo: string) => void
  /** Título de la vista actual. */
  titulo?: string
  /** Bajada del título, se esconde en pantallas angostas. */
  subtitulo?: ReactNode
  /** Acciones de nivel vista, a la izquierda del selector de período. */
  acciones?: ReactNode
}

export function Header({
  user,
  periodo,
  periodos,
  onPeriodoChange,
  titulo,
  subtitulo,
  acciones,
}: HeaderProps) {
  const periodoId = useId()

  return (
    <header className="sticky top-0 z-20 flex min-h-header shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-surface px-5 py-2">
      {titulo !== undefined ? (
        <div className="flex min-w-0 items-baseline gap-2">
          <h1 className="min-w-[8ch] truncate text-seccion text-text">
            {titulo}
          </h1>
          {subtitulo !== undefined ? (
            <span className="hidden text-meta text-muted-strong lg:inline">
              {subtitulo}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {acciones}

        <label htmlFor={periodoId} className="text-meta text-muted-strong">
          Período
        </label>
        <select
          id={periodoId}
          value={periodo}
          onChange={(evento) => onPeriodoChange(evento.target.value)}
          className="tabular h-7 rounded-md border border-border bg-surface px-2 text-meta font-medium text-text hover:border-n300"
        >
          {periodos.map((p) => (
            <option key={p} value={p}>
              {formatPeriodLabel(p)}
            </option>
          ))}
        </select>

        <span aria-hidden="true" className="h-6 w-px bg-border" />

        <div className="flex items-center gap-2">
          <div className="hidden text-right sm:block">
            <div className="text-meta font-semibold leading-tight text-text">
              {user.nombre}
            </div>
            <div className="rotulo leading-tight">{ROLE_LABELS[user.rol]}</div>
          </div>
          <span
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-micro font-bold text-accent-text"
          >
            {iniciales(user.nombre)}
          </span>
        </div>
      </div>
    </header>
  )
}
